import React, { useMemo } from 'react';
import { Box, CssBaseline, Typography, useMediaQuery, useTheme } from '@mui/material';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { AccountSettingsMenu, getIconForMenuItem } from '../../AccountSettings/AccountSettingsMenu';
import { getAccountSettingsItemLabel, isAccountNavItemActive } from '../../AccountSettings/accountSettingsNav.utils';
import { theme as appTheme } from '../../../constants/theme';
import { useThemeContext } from '../../../hook/useThemeManager';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import { ROUTES } from '../../../types/global/routes.types';

interface MenuItemType {
    name: string;
    route: string;
    label?: string;
}

interface AccountSettingsLayoutProps {
    menuItems: MenuItemType[];
}

const SIDEBAR_WIDTH_PX = 256;

const AccountSettingsLayout: React.FC<AccountSettingsLayoutProps> = ({ menuItems }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const muiTheme = useTheme();
    const isMdUp = useMediaQuery(muiTheme.breakpoints.up('md'));
    const { themeStyle } = useThemeContext();
    const t = appTheme[themeStyle];

    const activeItem = useMemo(
        () => menuItems.find(item => isAccountNavItemActive(location.pathname, item.route)),
        [location.pathname, menuItems],
    );

    const sectionTitle =
        activeItem?.name === 'settings' && isAccountNavItemActive(location.pathname, ROUTES.ACCOUNT_SETTINGS)
            ? 'Account Settings'
            : activeItem
              ? getAccountSettingsItemLabel(activeItem)
              : 'Account';

    const handleNav = (route: string) => {
        navigate(route);
    };

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                minHeight: '100vh',
                mt: { xs: 1, md: 2 },
            }}>
            <CssBaseline />

            <Box
                sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    alignItems: 'stretch',
                    width: '100%',
                    flex: 1,
                    gap: { xs: 0, md: 0 },
                }}>
                {isMdUp ? (
                    <Box
                        component="aside"
                        sx={{
                            width: SIDEBAR_WIDTH_PX,
                            flexShrink: 0,
                            alignSelf: 'flex-start',
                            position: 'sticky',
                            top: { md: 16, lg: 24 },
                            maxHeight: { md: 'calc(100vh - 96px)' },
                            overflowY: 'auto',
                            py: 3,
                            px: 2,
                            borderRight: `1px solid ${t.darkGray60}`,
                            bgcolor: t.pure_black,
                            borderRadius: { md: '0 12px 12px 0' },
                        }}>
                        <Box sx={{ mb: 4, px: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Box
                                    sx={{
                                        width: 40,
                                        height: 40,
                                        borderRadius: '12px',
                                        bgcolor: t.highlight,
                                        color: t.black,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        fontWeight: 900,
                                        fontSize: '1.1rem',
                                    }}>
                                    T
                                </Box>
                                <Box>
                                    <Typography
                                        sx={{
                                            color: t.highlight,
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            fontWeight: 800,
                                            fontSize: '0.8rem',
                                            letterSpacing: '-0.02em',
                                            lineHeight: 1.2,
                                        }}>
                                        Thspian
                                    </Typography>
                                    <Typography
                                        sx={{
                                            color: t.tag_white,
                                            opacity: 0.45,
                                            fontSize: '0.625rem',
                                            fontWeight: 700,
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.14em',
                                        }}>
                                        Account hub
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>

                        <Box component="nav" sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 1 }}>
                            {menuItems.map(item => {
                                const active = isAccountNavItemActive(location.pathname, item.route);
                                return (
                                    <Box
                                        key={item.route}
                                        role="button"
                                        tabIndex={0}
                                        onClick={() => handleNav(item.route)}
                                        onKeyDown={e => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                handleNav(item.route);
                                            }
                                        }}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1.5,
                                            py: 1.25,
                                            px: 2,
                                            borderRadius: '12px',
                                            cursor: 'pointer',
                                            color: active ? t.highlight : t.tag_white,
                                            opacity: active ? 1 : 0.55,
                                            bgcolor: active ? 'rgba(39, 39, 42, 0.45)' : 'transparent',
                                            transition: 'all 0.25s ease-in-out',
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            fontSize: '0.75rem',
                                            fontWeight: 500,
                                            textTransform: 'none',
                                            '&:hover': {
                                                bgcolor: 'rgba(39, 39, 42, 0.65)',
                                                color: active ? t.highlight : t.primary_white,
                                                opacity: 1,
                                            },
                                            '&:focus-visible': {
                                                outline: `1px solid ${t.highlight}`,
                                                outlineOffset: 2,
                                            },
                                        }}>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                color: 'inherit',
                                                '& .MuiSvgIcon-root': { fontSize: '1.25rem' },
                                            }}>
                                            {getIconForMenuItem(item.name)}
                                        </Box>
                                        {getAccountSettingsItemLabel(item)}
                                    </Box>
                                );
                            })}
                        </Box>
                    </Box>
                ) : null}

                <Box
                    sx={{
                        flex: 1,
                        minWidth: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        px: { xs: 2, sm: 3, md: 4 },
                    }}>
                    <AccountSettingsMenu
                        menuItems={menuItems}
                        onItemClick={item => handleNav(item.route)}
                    />

                    <Box
                        sx={{
                            borderBottom: `1px solid ${t.darkGray60}`,
                            py: 2,
                            mb: 2,
                        }}>
                        <Typography
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: { xs: '1.25rem', sm: '1.75rem' },
                                fontWeight: 800,
                                letterSpacing: '-0.04em',
                                color: t.filters_white,
                                textTransform: 'none',
                            }}>
                            {sectionTitle}
                        </Typography>
                    </Box>

                    <Box
                        sx={{
                            flexGrow: 1,
                            width: '100%',
                            maxWidth: 'min(1024px, 100%)',
                            mx: 'auto',
                            pb: 4,
                        }}>
                        <Outlet />
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export { AccountSettingsLayout };
